import { useMutation } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle } from '@material-ui/lab';
import ManualIdentityVerificationForm from './ManualIdentityVerificationForm';

const IDENTITY_VERIFICATION_CREATE = gql`
  mutation CreateIdentityVerificationSession(
    $input: IdentityVerificationSessionInput!
  ) {
    identityVerificationSessionCreate(input: $input) {
      redirectUrl
    }
  }
`;

interface IdentityVerification {
  status: string;
  stripeErrorReason?: string | null;
  rejectedReason?: string | null;
}

interface Props {
  compRegistration: {
    id: string;
    identityVerification?: IdentityVerification | null;
    identityVerifiedAt?: string | null;
  };
  currentUser?: {
    id?: string;
    name?: string;
    country?: string | null;
    countryName?: string | null;
  } | null;
  reload: () => void;
}

const IdentityVerificationStep = ({
  compRegistration,
  currentUser,
  reload,
}: Props) => {
  const failedVerificationMessage =
    compRegistration?.identityVerification?.stripeErrorReason || null;
  const processingVerification =
    compRegistration?.identityVerification?.status === 'processing';
  const manualSubmitted =
    compRegistration?.identityVerification?.status === 'manual_submitted';
  const isRejected =
    compRegistration?.identityVerification?.status === 'rejected';
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [createVerificationSession] = useMutation(IDENTITY_VERIFICATION_CREATE);

  useEffect(() => {
    if (!processingVerification) {
      return;
    }
    const interval = setInterval(() => {
      reload();
    }, 30000);
    return () => clearInterval(interval);
  }, [processingVerification, reload]);

  const handleVerify = () => {
    setVerifying(true);
    setError(null);

    const returnUrl = window.location.href;

    createVerificationSession({
      variables: {
        input: {
          compRegistrationId: compRegistration.id,
          returnUrl,
        },
      },
    })
      .then(({ data }) => {
        const redirectUrl =
          data?.identityVerificationSessionCreate?.redirectUrl;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          throw new Error('Failed to create verification session');
        }
      })
      .then(() => {
        reload();
      })
      .catch((err) => {
        setError(err.message || 'Verification failed. Please try again.');
        setVerifying(false);
      });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
      {manualSubmitted ? (
        <Alert severity="info" style={{ marginBottom: 24, maxWidth: 480 }}>
          <AlertTitle>Submitted for review</AlertTitle>
          Your documents have been submitted. The organizers will review them
          shortly and you will be notified when they are approved.
        </Alert>
      ) : isRejected ? (
        <Alert severity="warning" style={{ marginBottom: 24, maxWidth: 480 }}>
          <AlertTitle>Verification rejected</AlertTitle>
          <Box>Your previous submission was rejected.</Box>
          <Box>
            Reason: {compRegistration?.identityVerification?.rejectedReason}
          </Box>
          Please submit your documents again.
        </Alert>
      ) : failedVerificationMessage ? (
        <Alert severity="error" style={{ marginBottom: 24, maxWidth: 480 }}>
          <AlertTitle>Verification failed</AlertTitle>
          {failedVerificationMessage}
        </Alert>
      ) : processingVerification ? (
        <Alert severity="info" style={{ marginBottom: 24, maxWidth: 480 }}>
          <AlertTitle>Verification in progress</AlertTitle>
          We're checking your identity. This may take a few minutes.
        </Alert>
      ) : (
        <Alert severity="info" style={{ marginBottom: 24, maxWidth: 480 }}>
          <AlertTitle>Nationality verification required</AlertTitle>
          You'll need to provide a passport to validate your nationality.
        </Alert>
      )}
      {!manualSubmitted && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            variant="contained"
            color="primary"
            disabled={verifying || processingVerification}
            onClick={handleVerify}
          >
            {verifying ? 'Opening verification...' : 'Verify nationality'}
          </Button>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            mt={1}
            mb={1}
            style={{ gap: 20 }}
          >
            <Box width="20%" height="1px" bgcolor="#ccc" />
            or
            <Box width="20%" height="1px" bgcolor="#ccc" />
          </Box>
          {currentUser?.id && !processingVerification && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowManualForm(true)}
              style={{ marginTop: 8 }}
            >
              {compRegistration?.identityVerification
                ? 'Submit another document'
                : "I don't have a passport"}
            </Button>
          )}
        </Box>
      )}
      {!manualSubmitted && currentUser?.id && (
        <ManualIdentityVerificationForm
          open={showManualForm}
          onClose={() => setShowManualForm(false)}
          compRegistrationId={compRegistration.id}
          userName={currentUser?.name ?? ''}
          country={currentUser?.countryName ?? currentUser?.country ?? ''}
          onSuccess={reload}
        />
      )}
      {error && (
        <Box mt={2} color="error.main">
          {error}
        </Box>
      )}
    </Box>
  );
};

export default IdentityVerificationStep;
